import { useState } from "react";
import { useEffect } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Eye, UploadCloud, Trash2, Plus } from "lucide-react";

const emptyContractor = {
  business_name: "", category: "General Contractor", city: "", region: "", market: "US",
  address: "", phone: "", rating: 4.8, review_count: 0, verified: true, licensed_insured: true,
  years_in_business: 5, services: [], service_areas: [], photos: [], hours: [], description: "",
  logo_url: "", hero_image_url: "", testimonials: [], faq: [],
};

export default function AdminContractors() {
  const [contractors, setContractors] = useState(null);

  const load = () => api.get("/admin/contractors").then((res) => setContractors(res.data));
  useEffect(() => { load(); }, []);

  const publish = async (slug) => {
    try {
      const { data } = await api.post(`/admin/wordpress/publish-contractor/${slug}`);
      toast.success(`Published: ${data.link}`);
    } catch {
      toast.error("Publish failed");
    }
  };

  return (
    <div data-testid="admin-contractors-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-serif-heading font-bold text-navy">Contractors</h1>
        <EditDialog contractor={null} onSaved={load} />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead><TableHead>City</TableHead><TableHead>Market</TableHead>
              <TableHead>Rating</TableHead><TableHead>Verified</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contractors?.map((c) => (
              <TableRow key={c.slug} data-testid={`admin-contractor-row-${c.slug}`}>
                <TableCell className="font-medium text-navy">{c.business_name}</TableCell>
                <TableCell className="text-sm text-slate-500">{c.city}, {c.region}</TableCell>
                <TableCell><Badge variant="outline" data-testid={`admin-contractor-market-${c.slug}`}>{c.market || "US"}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{c.rating} ({c.review_count})</TableCell>
                <TableCell>{c.verified ? <Badge className="bg-green-100 text-green-700">Verified</Badge> : <Badge variant="outline">Unverified</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <PreviewDialog contractor={c} />
                  <EditDialog contractor={c} onSaved={load} />
                  <Button size="sm" variant="outline" onClick={() => publish(c.slug)} data-testid={`admin-contractor-publish-button-${c.slug}`}>
                    <UploadCloud className="w-3.5 h-3.5 mr-1" /> Publish
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PreviewDialog({ contractor: c }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`admin-contractor-preview-button-${c.slug}`}>
          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`admin-contractor-preview-dialog-${c.slug}`}>
        <DialogHeader>
          <DialogTitle>{c.business_name}</DialogTitle>
          <DialogDescription>Read-only summary of what will be published.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm text-slate-700">
          <p><strong>Category:</strong> {c.category}</p>
          <p><strong>Address:</strong> {c.address || `${c.city}, ${c.region}`}</p>
          <p><strong>Phone:</strong> {c.phone || "—"}</p>
          <p><strong>Rating:</strong> {c.rating} ({c.review_count} reviews)</p>
          <p><strong>Description:</strong> {c.description || "—"}</p>
          <p><strong>Services:</strong> {c.services?.length ? c.services.join(", ") : "—"}</p>
          <p><strong>Service Areas:</strong> {c.service_areas?.length ? c.service_areas.join(", ") : "—"}</p>
          <p><strong>Photos:</strong> {c.photos?.length || 0}</p>
          <p><strong>Hours:</strong> {c.hours?.length ? c.hours.map((h) => `${h.day}: ${h.hours}`).join(" · ") : "—"}</p>
          <p><strong>Testimonials:</strong> {c.testimonials?.length || 0}</p>
          <p><strong>FAQ:</strong> {c.faq?.length || 0}</p>
          <p><strong>Logo URL:</strong> {c.logo_url || "—"}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ contractor, onSaved }) {
  const isNew = !contractor;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(contractor || emptyContractor);
  const [saving, setSaving] = useState(false);
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/contractors", form);
      } else {
        await api.put(`/admin/contractors/${contractor.slug}`, form);
      }
      toast.success("Contractor saved");
      onSaved();
      setOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={isNew ? "default" : "outline"} className={isNew ? "bg-gold hover:bg-gold-600 text-navy font-bold" : ""} data-testid={isNew ? "admin-contractor-add-button" : `admin-contractor-edit-button-${contractor.slug}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> {isNew ? "Add Contractor" : "Edit"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-contractor-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Contractor" : `Edit — ${contractor.business_name}`}</DialogTitle>
          <DialogDescription>Full profile content, reviewed here before publishing to WordPress.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Business Name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="contractor-business-name-input" />
            {isNew && (
              <Input placeholder="Slug (e.g. acme-roofing-dallas-tx)" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="contractor-slug-input" />
            )}
            <Input placeholder="Category" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="contractor-category-input" />
            <Input placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="contractor-city-input" />
            <Input placeholder="Region/State" value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="contractor-region-input" />
            <Input placeholder="Address" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="contractor-address-input" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contractor-phone-input" />
            <Input type="number" step="0.1" placeholder="Rating" value={form.rating} onChange={(e) => set("rating", parseFloat(e.target.value))} data-testid="contractor-rating-input" />
            <Input type="number" placeholder="Review Count" value={form.review_count} onChange={(e) => set("review_count", parseInt(e.target.value || 0))} data-testid="contractor-review-count-input" />
            <Input type="number" placeholder="Years in Business" value={form.years_in_business} onChange={(e) => set("years_in_business", parseInt(e.target.value || 0))} data-testid="contractor-years-input" />
            <Input placeholder="Logo URL" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="contractor-logo-url-input" />
            <Input placeholder="Hero Image URL" value={form.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} className="col-span-2" data-testid="contractor-hero-image-input" />
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.verified} onCheckedChange={(v) => set("verified", v)} data-testid="contractor-verified-switch" />
              <label className="text-sm">Verified</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.licensed_insured} onCheckedChange={(v) => set("licensed_insured", v)} data-testid="contractor-licensed-switch" />
              <label className="text-sm">Licensed &amp; Insured</label>
            </div>
          </div>
          <Textarea placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="contractor-description-input" />
          <Input
            placeholder="Services (comma separated)"
            value={(form.services || []).join(", ")}
            onChange={(e) => set("services", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            data-testid="contractor-services-input"
          />
          <Input
            placeholder="Service Areas (comma separated)"
            value={(form.service_areas || []).join(", ")}
            onChange={(e) => set("service_areas", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            data-testid="contractor-service-areas-input"
          />
          <Textarea
            placeholder="Photo URLs (one per line)"
            value={(form.photos || []).join("\n")}
            onChange={(e) => set("photos", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
            rows={3}
            data-testid="contractor-photos-input"
          />

          <ListEditor
            label="Hours"
            items={form.hours || []}
            onChange={(items) => set("hours", items)}
            fields={[{ key: "day", placeholder: "Day (e.g. Monday)" }, { key: "hours", placeholder: "Hours (e.g. 8am - 6pm)" }]}
            testIdPrefix="hours"
          />
          <ListEditor
            label="Testimonials"
            items={form.testimonials || []}
            onChange={(items) => set("testimonials", items)}
            fields={[
              { key: "name", placeholder: "Name" }, { key: "location", placeholder: "Location" },
              { key: "rating", placeholder: "Rating (1-5)", type: "number" }, { key: "quote", placeholder: "Quote", full: true },
            ]}
            testIdPrefix="testimonial"
          />
          <ListEditor
            label="FAQ"
            items={form.faq || []}
            onChange={(items) => set("faq", items)}
            fields={[{ key: "question", placeholder: "Question" }, { key: "answer", placeholder: "Answer" }]}
            testIdPrefix="faq"
          />

          <Button onClick={save} disabled={saving} className="w-full bg-gold hover:bg-gold-600 text-navy font-bold" data-testid="contractor-save-button">
            {saving ? "Saving..." : "Save Contractor"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ListEditor({ label, items, onChange, fields, testIdPrefix }) {
  const addItem = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? 5 : ""]))]);
  const updateItem = (i, key, value) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-navy">{label}</label>
        <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid={`add-${testIdPrefix}-button`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 mb-2">
          <div className="grid grid-cols-2 gap-2 flex-1">
            {fields.map((f) => (
              <Input
                key={f.key}
                type={f.type || "text"}
                placeholder={f.placeholder}
                value={item[f.key] ?? ""}
                onChange={(e) => updateItem(i, f.key, f.type === "number" ? parseInt(e.target.value || 0) : e.target.value)}
                className={f.full ? "col-span-2" : ""}
                data-testid={`${testIdPrefix}-${f.key}-${i}`}
              />
            ))}
          </div>
          <button type="button" onClick={() => removeItem(i)} data-testid={`remove-${testIdPrefix}-${i}`}>
            <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600 mt-2" />
          </button>
        </div>
      ))}
    </div>
  );
}
